import {useCallback,useEffect,useRef,useState} from 'react';

// Only refresh reads. Never replay writes, reload the document, or poll hidden tabs.
export function useBackgroundRefresh(refresh,{enabled=true,interval=15000,scope=''}={}) {
 const latest=useRef(refresh);latest.current=refresh;
 const inFlight=useRef(false);
 useEffect(()=>{
  if(!enabled)return undefined;
  let stopped=false,timer,lastStarted=-Infinity;
  const available=()=>document.visibilityState!=='hidden'&&navigator.onLine!==false;
  const schedule=()=>{clearTimeout(timer);if(!stopped)timer=setTimeout(run,interval);};
  async function run(){
   clearTimeout(timer);
   if(stopped)return;
   if(inFlight.current||!available()||Date.now()-lastStarted<1000){schedule();return;}
   lastStarted=Date.now();
   inFlight.current=true;
   try{await latest.current();}catch{/* Page retains its own inline error and last successful data. */}
   finally{inFlight.current=false;schedule();}
  }
  const resume=()=>{if(available())run();};
  schedule();
  window.addEventListener('focus',resume);
  window.addEventListener('online',resume);
  document.addEventListener('visibilitychange',resume);
  return()=>{stopped=true;clearTimeout(timer);window.removeEventListener('focus',resume);window.removeEventListener('online',resume);document.removeEventListener('visibilitychange',resume);};
 },[enabled,interval,scope]);
}

const prefix='cybe-view-v1:';
function compatible(value,initial){
 if(typeof value!==typeof initial||value===null)return false;
 if(typeof initial==='string')return value.length<=512;
 if(typeof initial==='number')return Number.isInteger(value)&&value>=1&&value<=10000;
 if(typeof initial==='boolean')return true;
 if(Array.isArray(value)||typeof initial!=='object')return false;
 return Object.keys(value).length===Object.keys(initial).length&&Object.keys(initial).every(k=>compatible(value[k],initial[k]));
}
function restore(key,initial){
 try{
  const saved=JSON.parse(sessionStorage.getItem(prefix+key));
  if(saved&&Date.now()-saved.at>=0&&Date.now()-saved.at<86400000&&compatible(saved.value,initial))return saved.value;
 }catch{/* Storage can be unavailable. The workspace still works in memory. */}
 return initial;
}
// Store view preferences only: never API payloads, credentials, payment URLs, or mutations.
export function useViewState(key,initial){
 const initialRef=useRef(initial);initialRef.current=initial;
 const [entry,setEntry]=useState(()=>({key,value:restore(key,initial)}));
 const value=entry.key===key?entry.value:restore(key,initialRef.current);
 useEffect(()=>{if(entry.key!==key)setEntry({key,value:restore(key,initialRef.current)});},[entry.key,key]);
 useEffect(()=>{
  if(entry.key!==key)return;
  try{sessionStorage.setItem(prefix+key,JSON.stringify({at:Date.now(),value:entry.value}));}catch{}
 },[entry,key]);
 const setValue=useCallback(next=>setEntry(old=>{
  const previous=old.key===key?old.value:restore(key,initialRef.current);
  return {key,value:typeof next==='function'?next(previous):next};
 }),[key]);
 return [value,setValue];
}
